import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { StrategySimulationContext } from '../..';
import iconYesterday from '../../imgs/icon-yesterday.png';
import iconLastmonth from '../../imgs/icon-lastmonth.png';
import iconThismonth from '../../imgs/icon-thismonth.png';
import { ResolutionEnum, ResolutionEnumDisplay } from '../../type';
import dayjs, { Dayjs } from 'dayjs';
import { useRequest } from 'ahooks';
import { getChartOption } from './chart';
import { DatePicker, Empty, Radio, EchartsWithTheme, useTheme } from '@maxtropy/components';
import { apiV2MicrogridResolvedGetProfitReportPost } from '@maxtropy/intelligent-dispath-apis-v2';
import styles from './index.module.scss';
import tittleDecoration from '../../imgs/tittleDecoration.png';
import EachCard from '../EachCard';
import { Spin } from 'antd';
import { EchartsWithThemeRef } from '@maxtropy/components/es/components/EchartsWithTheme';

const IncomeAnalysis = () => {
  const { strategyId, lastStartTime } = useContext(StrategySimulationContext);

  const [resolution, setResolution] = useState<ResolutionEnum>(ResolutionEnum.DAY);
  const [date, setDate] = useState<Dayjs>(dayjs());
  const theme = useTheme();

  // 默认选择求解开始时间
  // useEffect(() => {
  //   if (lastStartTime) {
  //     setDate(dayjs(lastStartTime));
  //   }
  // }, [lastStartTime]);

  // 收益分析报表
  const { data, loading } = useRequest(
    () =>
      apiV2MicrogridResolvedGetProfitReportPost({
        strategyId,
        startTime: resolution === ResolutionEnum.DAY ? date.startOf('day').valueOf() : date.startOf('month').valueOf(),
        endTime: resolution === ResolutionEnum.DAY ? date.endOf('day').valueOf() : date.endOf('month').valueOf(),
        timeResolution: resolution,
      }).then(res => {
        return res;
      }),
    {
      ready: !!strategyId && !!date,
      refreshDeps: [strategyId, date],
    }
  );

  // 图表
  const chartOption = useMemo(() => getChartOption(resolution, data), [data, resolution]);

  const chartRef = useRef<EchartsWithThemeRef>();
  useEffect(() => {
    const handleResize = () => {
      if (chartRef.current) {
        chartRef.current.getInstance()?.resize();
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return (
    <div className={styles.container}>
      <div className={styles.headerWrapper}>
        <img src={tittleDecoration} alt="pic" className={styles.titleBorder} />
        <span className={styles.title}>收益分析</span>
      </div>
      <div className={styles.content_box}>
        <div className={styles.select_box}>
          <Radio.Group
            buttonStyle="solid"
            value={resolution}
            onChange={e => {
              setResolution(e.target.value);
              // setDate(lastStartTime ? dayjs(lastStartTime) : dayjs());
              setDate(dayjs());
            }}
          >
            <Radio.Button value={ResolutionEnum.DAY}>{ResolutionEnumDisplay[ResolutionEnum.DAY]}</Radio.Button>
            <Radio.Button value={ResolutionEnum.MONTH}>{ResolutionEnumDisplay[ResolutionEnum.MONTH]}</Radio.Button>
          </Radio.Group>
          <DatePicker
            style={{ width: 240 }}
            allowClear={false}
            picker={resolution === ResolutionEnum.DAY ? 'date' : 'month'}
            value={date}
            onChange={date => setDate(date)}
            disabledDate={current => {
              // 只允许选择 startDate ~ endDate 范围内的日期
              if (lastStartTime) {
                return (
                  current < dayjs(lastStartTime).startOf(resolution === ResolutionEnum.DAY ? 'days' : 'month') ||
                  current > dayjs().add(1, resolution === ResolutionEnum.DAY ? 'days' : 'month')
                );
              }
              return current > dayjs().add(1, 'days');
            }}
          />
        </div>
        <div className={styles.card_box}>
          {resolution === ResolutionEnum.DAY ? (
            <EachCard
              img={iconYesterday}
              lineOne={{
                label: '实际收益',
                value: data?.lastValue,
                unit: '元',
              }}
              lineTwo={{
                label: '算法预测收益',
                value: data?.lastPredictValue,
                unit: '元',
              }}
            />
          ) : (
            <>
              <EachCard
                img={iconLastmonth}
                lineOne={{
                  label: '实际收益',
                  value: data?.lastValue,
                  unit: '元',
                }}
                lineTwo={{
                  label: '算法预测收益',
                  value: data?.lastPredictValue,
                  unit: '元',
                }}
              />
              <EachCard
                img={iconThismonth}
                lineOne={{
                  label: '实际收益',
                  value: data?.currentValue,
                  unit: '元',
                }}
                lineTwo={{
                  label: '算法预测收益',
                  value: data?.currentPredictValue,
                  unit: '元',
                }}
              />
            </>
          )}
        </div>
        <div className={styles.chart_box}>
          <EchartsWithTheme
            empty={{
              isShowEmpty: !data?.actValues?.length && !data?.predictValues?.length,
              emptyNode: <Empty />,
            }}
            loading={loading}
            theme={theme}
            ref={d => {
              if (d) {
                chartRef.current = d;
              }
            }}
            style={{ height: 340, width: '100%' }}
            option={chartOption}
          />
        </div>
      </div>
    </div>
  );
};

export default IncomeAnalysis;
